import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Hourglass,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  type LucideIcon,
} from "lucide-react";

export type Tone = "good" | "wait" | "bad" | "neutral";
export type LadderState =
  | "QUEUED"
  | "UPLOADING"
  | "AWAITING_VERIFICATION"
  | "VERIFIED"
  | "BACKUP_PROTECTED"
  | "CLEANUP_ELIGIBLE";

/**
 * The protection ladder: the same words, order, icons and colours wherever a file or count appears,
 * on phone and web. Only ever driven by backend state (DESIGN.md §8).
 */
export const LADDER: { state: LadderState; label: string; tone: Tone; icon: LucideIcon }[] = [
  { state: "QUEUED", label: "Queued", tone: "wait", icon: Clock },
  { state: "UPLOADING", label: "Uploading", tone: "wait", icon: UploadCloud },
  { state: "AWAITING_VERIFICATION", label: "Awaiting verification", tone: "wait", icon: Hourglass },
  { state: "VERIFIED", label: "Verified", tone: "good", icon: CheckCircle2 },
  { state: "BACKUP_PROTECTED", label: "Backup protected", tone: "good", icon: ShieldCheck },
  { state: "CLEANUP_ELIGIBLE", label: "Cleanup eligible", tone: "good", icon: Sparkles },
];
const FAILED = { label: "Failed", tone: "bad" as Tone, icon: AlertTriangle };

export const ICON = { size: 16, strokeWidth: 1.5 } as const;

/** State as icon + word + colour, never colour alone. */
export function StatusPill({ state }: { state: LadderState | "FAILED" }) {
  const s = state === "FAILED" ? FAILED : LADDER.find((l) => l.state === state)!;
  const Icon = s.icon;
  return (
    <span className={`pill ${s.tone}`}>
      <Icon {...ICON} />
      {s.label}
    </span>
  );
}

/** A plain labelled pill for non-ladder statuses (e.g. "Not scanned"). */
export function Pill({ tone = "neutral", children }: { tone?: Tone; children: React.ReactNode }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

/**
 * Thin rounded bar. `value` is 0..1; `null` means unknown, which renders as indeterminate and is
 * never shown as complete.
 */
export function ProgressBar({
  value,
  label,
  tone = "primary",
}: {
  value: number | null;
  label: string;
  tone?: "primary" | "good" | "wait" | "bad";
}) {
  const pct = value === null ? null : Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div
      className={`progress ${tone}${pct === null ? " indeterminate" : ""}`}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      {...(pct === null ? {} : { "aria-valuenow": pct })}
    >
      <span style={{ width: pct === null ? undefined : `${pct}%` }} />
    </div>
  );
}
